import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, map } from 'rxjs';

type JwtClaims = Record<string, unknown> & {
  preferred_username?: string;
  email?: string;
  portfolio_id?: string;
  clearance?: string;
  roles?: string[];
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oidc = inject(OidcSecurityService);
  private readonly router = inject(Router);

  private readonly authState = toSignal(
    this.oidc.isAuthenticated$.pipe(map((value) => value.isAuthenticated)),
    { initialValue: false }
  );

  private readonly claimsState = toSignal(
    this.oidc.userData$.pipe(map((value) => (value.userData as JwtClaims | null) ?? null)),
    { initialValue: null }
  );
  private readonly accessTokenClaimsState = toSignal(
    this.oidc.getAccessToken().pipe(map((token) => this.decodeJwtPayload(token))),
    { initialValue: null }
  );

  readonly isAuthenticated = computed(() => this.authState());
  readonly claims = computed(() => this.claimsState());
  readonly username = computed(() => this.claims()?.preferred_username ?? this.claims()?.email ?? null);
  readonly portfolioId = computed(() => {
    const fromUserData = this.claimsState()?.portfolio_id;
    if (typeof fromUserData === 'string' && fromUserData.trim().length > 0) {
      return fromUserData;
    }

    const fromAccessToken = this.accessTokenClaimsState()?.portfolio_id;
    if (typeof fromAccessToken === 'string' && fromAccessToken.trim().length > 0) {
      return fromAccessToken;
    }

    return null;
  });

  readonly clearance = computed(() => {
    const fromUserData = this.claimsState()?.clearance;
    if (typeof fromUserData === 'string' && fromUserData.trim().length > 0) {
      return fromUserData;
    }
    const fromAccessToken = this.accessTokenClaimsState()?.clearance;
    if (typeof fromAccessToken === 'string' && fromAccessToken.trim().length > 0) {
      return fromAccessToken;
    }
    return 'OSTENSIVO';
  });

  checkAuth() {
    return this.oidc.checkAuth();
  }

  login(): void {
    this.oidc.authorize();
  }

  logout(): void {
    this.oidc.logoffAndRevokeTokens().subscribe({
      next: () => this.router.navigateByUrl('/'),
    });
  }

  getAccessToken(): Observable<string> {
    return this.oidc.getAccessToken();
  }

  /** Primeira role MG3P ativa — metadado de auditoria (sem PII). */
  perfilAuditoriaAtivo(): string | null {
    const granted = this.rolesConcedidas();
    const mg3p = [...granted].filter((r) => r.startsWith('ROLE_MG3P_')).sort();
    return mg3p[0] ?? null;
  }

  hasAnyRole(requiredRoles: string[]): boolean {
    if (requiredRoles.length === 0) {
      return true;
    }

    const userClaims = this.claims();
    const tokenClaims = this.accessTokenClaimsState();
    if (!userClaims && !tokenClaims) {
      return false;
    }

    const grantedRoles = this.rolesConcedidas();
    return requiredRoles.some((role) => grantedRoles.has(role));
  }

  private rolesConcedidas(): Set<string> {
    const userClaims = this.claims();
    const tokenClaims = this.accessTokenClaimsState();
    const realmRoles = [
      ...(userClaims?.realm_access?.roles ?? []),
      ...(tokenClaims?.realm_access?.roles ?? []),
    ];
    const clientRoles = [
      ...Object.values(userClaims?.resource_access ?? {}).flatMap((entry) => entry.roles ?? []),
      ...Object.values(tokenClaims?.resource_access ?? {}).flatMap((entry) => entry.roles ?? []),
    ];
    const plainRoles = [...(userClaims?.roles ?? []), ...(tokenClaims?.roles ?? [])];
    return new Set([...realmRoles, ...clientRoles, ...plainRoles]);
  }

  private decodeJwtPayload(token: string | null): JwtClaims | null {
    if (!token) {
      return null;
    }

    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padding = '='.repeat((4 - (base64.length % 4)) % 4);
      const json = atob(base64 + padding);
      return JSON.parse(json) as JwtClaims;
    } catch {
      return null;
    }
  }
}
